// v4 — Predictive ROAS
import React from 'react';
import { v4data, TIER } from './v4data';
import { Spark } from './viz';
import { PageHeader, DatePill, Card, StatCard, Pill, Insight, ChannelIcon, fmtX, fmtN, fmt$, roasTone, toneColor } from './v4ui';
import MarkPopup from './MarkPopup';
import './dashboard.scss';

function predict(series) {
  const last7 = series.slice(-7), prev7 = series.slice(-14, -7);
  const a = last7.reduce((x, y) => x + y, 0) / Math.max(1, last7.length);
  const b = prev7.reduce((x, y) => x + y, 0) / Math.max(1, prev7.length) || a;
  const chg = (a - b) / (b || 1);
  return { dir: chg >= 0 ? 'increase' : 'decrease', conf: Math.round(Math.min(96, Math.max(53, 60 + Math.abs(chg) * 170))), chg };
}
const rec = (r) => (r >= 4.5 ? { l: 'Scale', c: 'good' } : r >= 2.5 ? { l: 'Maintain', c: 'mid' } : { l: 'Review', c: 'low' });

export default function PredictiveV4() {
  const D = v4data(30);
  const rows = D.ds.campaigns.map((c) => {
    const m = D.campSum(c);
    const series = D.campSeries(c, D.dates[Math.max(0, D.last - 13)], D.to).map((d) => d.totals.roas);
    return { c, m, series, p: predict(series), roas: m.roas };
  }).sort((a, b) => b.roas - a.roas);

  const toScale = rows.filter((r) => r.roas >= 4.5).length;
  const avgConf = Math.round(rows.reduce((a, r) => a + r.p.conf, 0) / rows.length);
  const predBlended = D.cur.roas * (1 + (rows.reduce((a, r) => a + r.p.chg, 0) / rows.length) * 0.4);
  const best = rows[0];

  return (
    <div className="v4-dash">
      <PageHeader title="Predictive ROAS" sub="7-day performance forecast · per campaign" live right={<DatePill />} />

      <MarkPopup inline context="predictive" />

      <section className="v4-statstrip">
        <StatCard label="Blended ROAS (30d)" value={fmtX(D.cur.roas)} valueColor="#5A54F3" />
        <StatCard label="Predicted ROAS (next 7d)" value={fmtX(predBlended)} valueColor="#8AE8BD" />
        <StatCard label="Avg Confidence" value={avgConf + '%'} valueColor="#38bdf8" />
        <StatCard label="Campaigns to Scale" value={String(toScale)} valueColor="#7A75F6" />
      </section>

      <Card className="v4-table-card" pad={false}>
        <div className="v4-table-head"><h3 style={{ margin: 0 }}>Campaign Forecast</h3><span className="v4-muted">{rows.length} campaigns · trailing 7-day trend</span></div>
        <div className="v4-table">
          <div className="v4-tr v4-tr-pred v4-th">
            <span className="c-name">Campaign</span><span>ROAS</span><span className="c-spark">Momentum</span>
            <span className="c-pred">Predicted (7d)</span><span className="c-conf">Confidence</span><span className="c-rec">Action</span>
          </div>
          {rows.map(({ c, m, series, p, roas }) => {
            const rc = rec(roas), t = TIER[c.tier];
            return (
              <div className="v4-tr v4-tr-pred" key={c.id}>
                <span className="c-name"><ChannelIcon color={t.color} /><span className="nm"><b>{c.name}</b><em style={{ color: t.color }}>{t.label}</em></span></span>
                <span className={'roas ' + rc.c}>{fmtX(roas)}</span>
                <span className="c-spark"><Spark data={series} color={toneColor(rc.c)} w={92} h={26} fill={false} /></span>
                <span className="c-pred"><b className={p.dir === 'increase' ? 'up' : 'down'}>{p.dir === 'increase' ? '▲' : '▼'} {p.dir}</b></span>
                <span className="c-conf"><span className="v4-conf"><span style={{ width: p.conf + '%', background: `linear-gradient(90deg, ${toneColor(rc.c)}, ${toneColor(rc.c)}99)` }} /></span><em>{p.conf}%</em></span>
                <span className="c-rec"><Pill tone={rc.c}>{rc.l}</Pill></span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
